import React, { useEffect, useState } from 'react';
import { api } from '@/App';
import usePeriodChange from '@/hooks/usePeriodChange';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableFooter } from '@/components/ui/table';
import { toast } from 'sonner';
import { History as HistoryIcon } from 'lucide-react';

const fmtNum = (v, digits = 0) => {
  if (v === null || v === undefined || isNaN(v) || !isFinite(v)) return '-';
  return new Intl.NumberFormat('id-ID', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits
  }).format(v);
};

const fmtInt = (v) => fmtNum(Math.round(v || 0), 0);
const fmtPct = (v) => {
  if (v === null || v === undefined || isNaN(v) || !isFinite(v)) return '-';
  return `${v.toFixed(2)}%`;
};

const extractYearFromPeriod = (period) => {
  if (!period) return null;
  // Prefer months array
  const months = period.months || [];
  if (months.length > 0) {
    const firstMonth = months[0];
    if (typeof firstMonth === 'string' && firstMonth.includes('-')) {
      const y = parseInt(firstMonth.split('-')[0], 10);
      if (!isNaN(y)) return y;
    }
  }
  // Fallback: parse name "Bulan YYYY"
  if (period.name) {
    const parts = period.name.split(' ');
    const last = parts[parts.length - 1];
    const y = parseInt(last, 10);
    if (!isNaN(y)) return y;
  }
  return null;
};

const BranchHistory = ({ branchId }) => {
  const [year, setYear] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const resolveYearFromSelectedPeriod = () => {
    const periodId = localStorage.getItem('selectedPeriodId');
    if (!periodId) {
      setYear(new Date().getFullYear());
      return;
    }
    api.get('/periods')
      .then((res) => {
        const period = (res.data || []).find((p) => p.id === periodId);
        const y = extractYearFromPeriod(period);
        setYear(y || new Date().getFullYear());
      })
      .catch(() => setYear(new Date().getFullYear()));
  };

  // Determine year from selected period (initial + on periodChanged)
  useEffect(() => {
    resolveYearFromSelectedPeriod();
  }, []);

  usePeriodChange(() => resolveYearFromSelectedPeriod());

  useEffect(() => {
    if (!branchId || !year) return;
    let cancelled = false;
    setLoading(true);
    api.get(`/branches/${branchId}/yearly-history`, { params: { year } })
      .then((res) => { if (!cancelled) setRows(res.data || []); })
      .catch((err) => {
        if (!cancelled) {
          toast.error(err.response?.data?.detail || 'Gagal memuat history cabang');
          setRows([]);
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [branchId, year]);

  const totals = rows.reduce((acc, r) => {
    acc.penjualan_gr += r.penjualan_gr || 0;
    return acc;
  }, { penjualan_gr: 0 });

  return (
    <Card className="border-2 border-amber-200" data-testid="branch-history-card">
      <CardHeader className="bg-gradient-to-r from-amber-50 to-yellow-50">
        <CardTitle className="text-lg font-bold text-slate-800 flex items-center gap-2">
          <HistoryIcon size={18} />
          History Cabang — Tahun {year || '...'}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-5">
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-amber-500" />
          </div>
        ) : rows.length === 0 ? (
          <div className="text-center py-10 text-slate-500 text-sm">
            Tidak ada data untuk tahun {year}.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid="branch-history-table">
              <TableHeader>
                <TableRow className="bg-amber-100">
                  <TableHead className="text-xs font-bold text-slate-700">Bulan</TableHead>
                  <TableHead className="text-xs font-bold text-slate-700 text-right">Penjualan (gr)</TableHead>
                  <TableHead className="text-xs font-bold text-slate-700 text-right">Laba / gr</TableHead>
                  <TableHead className="text-xs font-bold text-slate-700 text-right">Biaya / gr</TableHead>
                  <TableHead className="text-xs font-bold text-slate-700 text-right">Kadar %</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={`${r.period_id}-${r.month}`} data-testid={`branch-history-row-${r.month}`}>
                    <TableCell className="text-xs text-slate-900">{r.month_label}</TableCell>
                    <TableCell className="text-xs text-right text-slate-900">{fmtInt(r.penjualan_gr)}</TableCell>
                    <TableCell className="text-xs text-right text-slate-900">{fmtInt(r.laba_per_gr)}</TableCell>
                    <TableCell className="text-xs text-right text-slate-900">{fmtInt(r.biaya_per_gr)}</TableCell>
                    <TableCell className="text-xs text-right text-slate-900">{fmtPct(r.kadar_percent)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <TableFooter>
                <TableRow className="bg-amber-100 border-t-2 border-amber-600 font-bold">
                  <TableCell className="text-xs text-slate-800">TOTAL</TableCell>
                  <TableCell className="text-xs text-right text-slate-800">{fmtInt(totals.penjualan_gr)}</TableCell>
                  <TableCell className="text-xs text-right text-slate-500">—</TableCell>
                  <TableCell className="text-xs text-right text-slate-500">—</TableCell>
                  <TableCell className="text-xs text-right text-slate-500">—</TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default BranchHistory;
